import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import api from '../../../utils/api';
import { ShieldCheck, RefreshCw, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';

const STATUS_STYLES = {
  active: { bg: 'bg-green-50', border: 'border-green-200', dot: 'bg-green-500', text: 'text-green-700', label: 'Active' },
  pending: { bg: 'bg-yellow-50', border: 'border-yellow-200', dot: 'bg-yellow-500', text: 'text-yellow-700', label: 'Pending' },
  expired: { bg: 'bg-red-50', border: 'border-red-200', dot: 'bg-red-500', text: 'text-red-700', label: 'Expired' },
  none: { bg: 'bg-slate-50', border: 'border-slate-200', dot: 'bg-slate-400', text: 'text-slate-500', label: 'Not Checked' },
};

const ProviderCard = ({ name, data, onRun, running }) => {
  const s = STATUS_STYLES[data?.status] || STATUS_STYLES.none;
  const checked = data?.checked_at ? new Date(data.checked_at).toLocaleDateString() : null;

  return (
    <div className={`${s.bg} border ${s.border} rounded-xl p-4`} data-testid={`compliance-${name.toLowerCase()}`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div className={`w-2 h-2 rounded-full ${s.dot} ${data?.status === 'active' ? 'animate-pulse' : ''}`} />
          <p className="text-sm font-semibold text-slate-800">{name}</p>
        </div>
        <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${s.bg} ${s.text} border ${s.border}`}>{s.label}</span>
      </div>

      {data?.details && (
        <div className="grid grid-cols-2 gap-1.5 mb-3">
          {Object.entries(data.details).map(([key, value]) => (
            <div key={key} className="text-[10px]">
              <span className="text-slate-500">{key.replace(/_/g, ' ')}: </span>
              <span className={`font-medium ${typeof value === 'boolean' ? (value ? 'text-green-700' : 'text-red-700') : 'text-slate-800'}`}>
                {typeof value === 'boolean' ? (value ? 'Yes' : 'No') : String(value)}
              </span>
            </div>
          ))}
        </div>
      )}

      {checked && <p className="text-[10px] text-slate-400 mb-2">Last checked: {checked}</p>}

      <button
        onClick={onRun}
        disabled={running}
        className="w-full py-1.5 text-xs font-medium rounded-lg border border-slate-300 hover:bg-white transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
        data-testid={`run-${name.toLowerCase()}-check`}
      >
        {running ? <RefreshCw className="h-3 w-3 animate-spin" /> : <ShieldCheck className="h-3 w-3" />}
        {running ? 'Checking...' : data?.status === 'active' ? 'Re-Check' : 'Run Check'}
      </button>
    </div>
  );
};

const ComplianceSection = () => {
  const [rmis, setRmis] = useState(null);
  const [highway, setHighway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [runningRmis, setRunningRmis] = useState(false);
  const [runningHighway, setRunningHighway] = useState(false);

  const fetchStatus = useCallback(async () => {
    try {
      const { data } = await api.get('/compliance/status');
      setRmis(data.rmis);
      setHighway(data.highway);
    } catch { /* ignore - will show "Not Checked" */ }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const runRmis = async () => {
    setRunningRmis(true);
    try {
      const { data } = await api.post('/compliance/rmis/check');
      setRmis(data.data);
      toast.success('RMIS check completed');
    } catch { toast.error('RMIS check failed'); }
    finally { setRunningRmis(false); }
  };

  const runHighway = async () => {
    setRunningHighway(true);
    try {
      const { data } = await api.post('/compliance/highway/check');
      setHighway(data.data);
      toast.success('Highway check completed');
    } catch { toast.error('Highway check failed'); }
    finally { setRunningHighway(false); }
  };

  if (loading) {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <div className="animate-pulse space-y-3">
          <div className="h-4 bg-slate-200 rounded w-1/3" />
          <div className="h-16 bg-slate-100 rounded" />
          <div className="h-16 bg-slate-100 rounded" />
        </div>
      </div>
    );
  }

  return (
    <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 }}
      className="bg-white border border-slate-200 rounded-xl p-5" data-testid="compliance-section"
    >
      <h3 className="text-sm font-bold text-slate-900 mb-3 flex items-center gap-2">
        <ShieldCheck className="h-4 w-4 text-green-500" /> Carrier Compliance
      </h3>
      <div className="space-y-3">
        <ProviderCard name="RMIS" data={rmis} onRun={runRmis} running={runningRmis} />
        <ProviderCard name="Highway" data={highway} onRun={runHighway} running={runningHighway} />
      </div>
      <p className="text-[10px] text-slate-400 mt-3 flex items-center gap-1">
        <AlertCircle className="h-3 w-3" /> MOCKED — Real integration pending
      </p>
    </motion.div>
  );
};

export default ComplianceSection;
